/* Hook de autenticação — JWT em localStorage + sync entre abas */
import { useState, useEffect, useCallback } from 'react';
import { getStoredJwt, storeJwt, clearJwt } from '../api/client';
import { requestCode, verifyCode } from '../api/auth';
import type { AuthState, AuthUser, Locale } from '../types';

/** Decodifica payload JWT sem verificar assinatura (só client-side) */
function decodeJwtPayload(jwt: string): AuthUser | null {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return {
      sub: payload.sub ?? '',
      email: payload.email ?? '',
      tier: payload.tier ?? 'free',
      exp: payload.exp ?? 0,
    };
  } catch {
    return null;
  }
}

/** true se JWT ainda está dentro da validade */
function isJwtValid(user: AuthUser): boolean {
  return user.exp * 1000 > Date.now();
}

export interface UseAuthReturn extends AuthState {
  /** Solicita código de magic link por e-mail */
  requestMagicCode: (email: string, locale: Locale) => Promise<void>;
  /**
   * Verifica código e faz login.
   * `anonId` (opcional, trial v2): migra o trial anônimo pro usuário —
   * backend copia relógio/casos/memória de conversa (TRIAL_V2_API.md §2).
   */
  login: (email: string, code: string, anonId?: string) => Promise<void>;
  /** Limpa sessão */
  logout: () => void;
}

export function useAuth(): UseAuthReturn {
  const [state, setState] = useState<AuthState>({
    user: null,
    jwt: null,
    loading: true,
  });

  // Inicializa a partir do localStorage
  useEffect(() => {
    const jwt = getStoredJwt();
    if (jwt) {
      const user = decodeJwtPayload(jwt);
      if (user && isJwtValid(user)) {
        setState({ user, jwt, loading: false });
        return;
      }
      // JWT expirado — limpa
      clearJwt();
    }
    setState((prev) => ({ ...prev, loading: false }));
  }, []);

  // Sync entre abas via evento storage
  useEffect(() => {
    function handleStorage(event: StorageEvent) {
      if (event.key !== 'drpera-jwt') return;
      if (event.newValue) {
        const user = decodeJwtPayload(event.newValue);
        if (user && isJwtValid(user)) {
          setState({ user, jwt: event.newValue, loading: false });
          return;
        }
      }
      // JWT removido em outra aba
      setState({ user: null, jwt: null, loading: false });
    }
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const requestMagicCode = useCallback(async (email: string, locale: Locale) => {
    await requestCode(email, locale, 'web-trial');
  }, []);

  const login = useCallback(async (email: string, code: string, anonId?: string) => {
    const data = await verifyCode(email, code, anonId);
    storeJwt(data.jwt);
    const user = decodeJwtPayload(data.jwt);
    if (!user) throw new Error('JWT inválido retornado pelo servidor');
    setState({ user, jwt: data.jwt, loading: false });
  }, []);

  const logout = useCallback(() => {
    clearJwt();
    setState({ user: null, jwt: null, loading: false });
  }, []);

  return { ...state, requestMagicCode, login, logout };
}
